import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Finishes a Xero or MYOB sign-in when the provider sends the browser back.
 *
 * The provider's redirect reaches the server with no session, so the server
 * connects nothing there: it hands `code` and `state` on to this page, and the
 * person signed in here completes it. The server only accepts that from the
 * person who started the sign-in, in the organisation they started it for.
 * Otherwise a sign-in link sent to somebody at another company would connect
 * that company's books to the sender's organisation.
 *
 * Both values are taken out of the address bar before the request is made.
 * Returns true while the sign-in is being completed.
 */
export function useOAuthReturn(
  complete: (code: string, state: string) => Promise<unknown>,
  onConnected: () => void,
  onError: (message: string) => void,
  fallback: string,
): boolean {
  const [searchParams, setSearchParams] = useSearchParams();
  const [finishing, setFinishing] = useState(false);
  // One completion per return: the code is single-use, and an effect can run
  // twice for one render.
  const started = useRef(false);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (!code || !state || started.current) return;
    started.current = true;

    const next = new URLSearchParams(searchParams);
    // `error` rides along only for app bundles from before this hook existed.
    for (const key of ['code', 'state', 'error']) next.delete(key);
    setSearchParams(next, { replace: true });

    setFinishing(true);
    complete(code, state)
      .then(onConnected)
      .catch((e: unknown) => onError(e instanceof Error && e.message ? e.message : fallback))
      .finally(() => {
        started.current = false;
        setFinishing(false);
      });
  }, [searchParams, setSearchParams, complete, onConnected, onError, fallback]);

  return finishing;
}

/** True when the page was opened by a provider's redirect that still has to be completed. */
export const isOAuthReturn = (searchParams: URLSearchParams): boolean =>
  searchParams.has('code') && searchParams.has('state');
